import React, { useState } from 'react';
import { Plus, Trash2, Edit2, Check, X, Lock, ChevronDown, ChevronUp, Code } from 'lucide-react';

const TIPO_BADGE = {
  ingreso:  'bg-green-100 text-green-700',
  deduccion: 'bg-red-100 text-red-700'
};

// Variables disponibles en el contexto de cálculo del motor de nómina
const VARIABLES_REFERENCIA = [
  { grupo: 'Ingresos',      vars: [
    { nombre: 'salario_base',   desc: 'Salario base del período' },
    { nombre: 'horas_extras',   desc: 'Monto de horas extra' },
    { nombre: 'comisiones',     desc: 'Comisiones del período' },
    { nombre: 'vacaciones',     desc: 'Pago de vacaciones' },
    { nombre: 'incentivos',     desc: 'Incentivos del período' },
    { nombre: 'bonificaciones', desc: 'Bonificaciones' },
    { nombre: 'otros_ingresos', desc: 'Otros ingresos variables' },
    { nombre: 'salario_bruto',  desc: 'Total de ingresos (base + variables)' },
  ]},
  { grupo: 'TSS Empleado',  vars: [
    { nombre: 'afp_empleado',       desc: 'AFP neto descontado al empleado' },
    { nombre: 'afp_empleado_bruto', desc: 'AFP bruto (obligación legal)' },
    { nombre: 'sfs_empleado',       desc: 'SFS neto descontado al empleado' },
    { nombre: 'sfs_empleado_bruto', desc: 'SFS bruto (obligación legal)' },
    { nombre: 'tss_empleado',       desc: 'Total TSS descontado (AFP+SFS neto)' },
    { nombre: 'tss_absorbido',      desc: 'TSS del empleado asumido por empresa' },
  ]},
  { grupo: 'TSS Empleador', vars: [
    { nombre: 'afp_empleador', desc: 'AFP aporte empleador' },
    { nombre: 'sfs_empleador', desc: 'SFS aporte empleador' },
    { nombre: 'srl_empleador', desc: 'SRL aporte empleador' },
    { nombre: 'infotep',       desc: 'INFOTEP aporte empleador' },
    { nombre: 'tss_empleador', desc: 'Total TSS empleador' },
  ]},
  { grupo: 'Deducciones',   vars: [
    { nombre: 'isr',                desc: 'Impuesto Sobre la Renta del período' },
    { nombre: 'descuento_prestamo', desc: 'Cuota de préstamo del período' },
    { nombre: 'otras_deducciones',  desc: 'Suma de otras deducciones' },
    { nombre: 'capita_descontada',  desc: 'Cápita de dependientes (si aplica)' },
  ]},
];

const emptyForm = {
  codigo:    '',
  nombre:    '',
  tipo:      'ingreso',
  formula:   '',
  afecta_ss:  false,
  afecta_isr: false,
  activo:    true
};

export default function ConceptosTab({ conceptos, createConcepto, updateConcepto, deleteConcepto }) {
  const [showForm,    setShowForm]    = useState(false);
  const [form,        setForm]        = useState(emptyForm);
  const [editingId,   setEditingId]   = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const [showVarRef,  setShowVarRef]  = useState(false);

  const handleOpenCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError('');
    setShowForm(true);
    setShowVarRef(false);
  };

  const handleOpenEdit = (c) => {
    setForm({
      codigo:    c.codigo,
      nombre:    c.nombre,
      tipo:      c.tipo,
      formula:   c.formula || '',
      afecta_ss:  c.afecta_ss,
      afecta_isr: c.afecta_isr,
      activo:    c.activo
    });
    setEditingId(c.id);
    setError('');
    setShowForm(true);
    setShowVarRef(false);
  };

  const handleSave = async () => {
    if (!form.codigo || !form.nombre) {
      setError('Código y nombre son obligatorios');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await updateConcepto(editingId, form);
      } else {
        await createConcepto(form);
      }
      setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este concepto?')) return;
    try {
      await deleteConcepto(id);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const insertVariable = (varName) => {
    setForm(f => ({ ...f, formula: f.formula ? f.formula + ' + ' + varName : varName }));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={handleOpenCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold text-sm"
        >
          <Plus className="w-4 h-4" />
          Nuevo Concepto
        </button>
      </div>

      {showForm && (
        <div className="border border-blue-200 bg-blue-50 rounded-xl p-5 space-y-4">
          <h4 className="font-semibold text-blue-800">{editingId ? 'Editar Concepto' : 'Nuevo Concepto'}</h4>
          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</p>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Código *</label>
              <input
                value={form.codigo}
                onChange={e => setForm(f => ({ ...f, codigo: e.target.value.toUpperCase() }))}
                disabled={!!editingId}
                maxLength={20}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100"
                placeholder="TRANS"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre *</label>
              <input
                value={form.nombre}
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="Bono de Transporte"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Tipo *</label>
              <select
                value={form.tipo}
                onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="ingreso">Ingreso</option>
                <option value="deduccion">Deducción</option>
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-gray-600">Fórmula</label>
                <button
                  type="button"
                  onClick={() => setShowVarRef(v => !v)}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold"
                >
                  <Code className="w-3 h-3" />
                  Variables disponibles
                  {showVarRef ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>
              <input
                value={form.formula}
                onChange={e => setForm(f => ({ ...f, formula: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="salario_base * 0.05"
              />
              <p className="text-xs text-gray-400 mt-1">
                Ej: <code className="bg-gray-100 px-1 rounded">salario_base * 0.05</code> ·
                <code className="bg-gray-100 px-1 rounded ml-1">comisiones + incentivos</code>
              </p>
            </div>
          </div>

          {/* Panel de referencia de variables */}
          {showVarRef && (
            <div className="border border-blue-200 bg-white rounded-xl p-4 space-y-3">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Variables disponibles en la fórmula</p>
              <p className="text-xs text-gray-500">Haz clic en una variable para insertarla en la fórmula.</p>
              <div className="grid grid-cols-2 gap-3">
                {VARIABLES_REFERENCIA.map(grupo => (
                  <div key={grupo.grupo}>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{grupo.grupo}</p>
                    <div className="space-y-1">
                      {grupo.vars.map(v => (
                        <button
                          key={v.nombre}
                          type="button"
                          onClick={() => insertVariable(v.nombre)}
                          className="w-full text-left px-2 py-1 rounded hover:bg-blue-50 group transition-colors"
                        >
                          <span className="font-mono text-xs text-blue-700 group-hover:text-blue-900">{v.nombre}</span>
                          <span className="text-xs text-gray-400 ml-2">{v.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.afecta_ss}
                onChange={e => setForm(f => ({ ...f, afecta_ss: e.target.checked }))}
                className="w-4 h-4 accent-blue-600"
              />
              Afecta Seguridad Social
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.afecta_isr}
                onChange={e => setForm(f => ({ ...f, afecta_isr: e.target.checked }))}
                className="w-4 h-4 accent-blue-600"
              />
              Afecta ISR
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.activo}
                onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))}
                className="w-4 h-4 accent-blue-600"
              />
              Activo
            </label>
          </div>

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100"
            >
              <X className="w-4 h-4 inline mr-1" />Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              <Check className="w-4 h-4 inline mr-1" />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      <div className="overflow-hidden border border-gray-200 rounded-xl">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left  text-xs font-bold uppercase text-gray-600">Código</th>
              <th className="px-4 py-3 text-left  text-xs font-bold uppercase text-gray-600">Nombre</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase text-gray-600">Tipo</th>
              <th className="px-4 py-3 text-left  text-xs font-bold uppercase text-gray-600">Fórmula</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase text-gray-600">SS</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase text-gray-600">ISR</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase text-gray-600">Estado</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase text-gray-600">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {conceptos.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">
                  No hay conceptos configurados
                </td>
              </tr>
            ) : (
              conceptos.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">{c.codigo}</span>
                    {c.es_sistema && (
                      <span className="ml-2" title="Concepto del sistema">
                        <Lock className="w-3 h-3 inline text-gray-400" />
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800">{c.nombre}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TIPO_BADGE[c.tipo]}`}>
                      {c.tipo === 'ingreso' ? 'Ingreso' : 'Deducción'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {c.formula ? (
                      <code className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-mono">{c.formula}</code>
                    ) : c.es_sistema ? (
                      <span className="text-xs text-gray-400 italic">por tipo deducción</span>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {c.afecta_ss  ? <Check className="w-4 h-4 text-green-600 mx-auto" /> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {c.afecta_isr ? <Check className="w-4 h-4 text-green-600 mx-auto" /> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${c.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {c.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => handleOpenEdit(c)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      {!c.es_sistema && (
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
